import { useQuery } from '@apollo/client';
import React, { ReactNode, useMemo } from 'react';
import {
  Breadcrumbs as BreadcrumbsComponent,
  Link,
  Typography,
} from '@mui/material';
import {
  Link as BaseLink,
  Location,
  useLocation,
  useParams,
} from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import {
  GET_MY_FIND_QUERY,
  GET_MY_SITE_QUERY,
  GET_FIND_GROUP_QUERY,
  GET_OBJECT_GROUP_QUERY,
  GET_MY_FIELD_SESSION_SUMMARY_QUERY,
  GET_MY_CONTACT_QUERY,
} from '../../gql';

const breadcrumbsStyles = {
  '& .MuiBreadcrumbs-ol': {
    alignItems: 'normal',
    flexWrap: 'nowrap',
  },
  '& .MuiBreadcrumbs-li': {
    flexShrink: 1,
    maxWidth: { xs: '80px', sm: 'unset' },
  },
  '& .MuiBreadcrumbs-li > a': {
    display: 'block',
  },
};
const adminSectionMessages = defineMessages({
  users: { id: 'admin.menu.users', defaultMessage: 'Users' },
  imports: { id: 'admin.menu.imports', defaultMessage: 'Imports' },
  activity: { id: 'admin.menu.activity', defaultMessage: 'Activity log' },
});

const getSectionCrumbs = (
  location: Location,
  siteName?: string,
  find?: { id: string; siteId?: string },
  objectGroupName?: string,
  findGroupName?: string,
  fieldSessionName?: string,
  contactName?: string
): ReactNode[] => {
  const parts = location.pathname.slice(1).split('/');
  let crumbs = [];

  if (parts[0] === 'o') {
    crumbs = [
      ...crumbs,
      <Link
        to="/o"
        underline="hover"
        key="list-objects"
        color="inherit"
        component={BaseLink}
      >
        <FormattedMessage id="map.tab.objects" defaultMessage="Objects" />
      </Link>,
    ];

    if (parts[1] === 'groups') {
      crumbs = [
        ...crumbs,
        <Link
          to="/o/groups"
          underline="hover"
          key="list-object-groups"
          color="inherit"
          component={BaseLink}
        >
          <FormattedMessage id="finds.menu.groups" defaultMessage="Groups" />
        </Link>,
      ];
      if (parts[2]) {
        return [
          ...crumbs,
          <Typography noWrap key={`object-group-${parts[2]}`}>
            {objectGroupName || parts[2]}
          </Typography>,
        ];
      }
    }

    return crumbs;
  }

  if (parts[0] === 's') {
    // we are in the site namespace
    crumbs = [
      ...crumbs,
      <Link
        to="/s"
        underline="hover"
        key="list-sites"
        color="inherit"
        component={BaseLink}
      >
        <FormattedMessage id="map.tab.sites" defaultMessage="Sites" />
      </Link>,
    ];

    if (!parts[1]) {
      return crumbs;
    }

    if (!siteName) {
      return [
        ...crumbs,
        <Typography noWrap key="create-site">
          <FormattedMessage
            id="site.action.create"
            defaultMessage="Create site"
          />
        </Typography>,
      ];
    }

    return [
      ...crumbs,
      <Typography noWrap key={`site-${parts[1]}`}>
        {siteName}
      </Typography>,
    ];

    //${parts[1].charAt(0).toUpperCase()}${parts[1].slice(1)}` for subsection
  }

  if (parts[0] === 'finds') {
    crumbs = [
      ...crumbs,
      <Link
        to="/finds"
        underline="hover"
        key="list-finds"
        color="inherit"
        component={BaseLink}
      >
        <FormattedMessage id="map.tab.finds" defaultMessage="Finds" />
      </Link>,
    ];

    if (parts[1] === 'groups') {
      crumbs = [
        ...crumbs,
        <Link
          to="/finds/groups"
          underline="hover"
          key="list-find-groups"
          color="inherit"
          component={BaseLink}
        >
          <FormattedMessage id="finds.menu.groups" defaultMessage="Groups" />
        </Link>,
      ];

      if (parts[2]) {
        return [
          ...crumbs,
          <Typography noWrap key={`find-group-${parts[2]}`}>
            {findGroupName || parts[2]}
          </Typography>,
        ];
      }
    }

    return crumbs;
  }

  if (parts[0] === 'field-sessions') {
    const pages = new URLSearchParams(location.search).get('pages');
    const listPath = pages
      ? `/field-sessions?pages=${encodeURIComponent(pages)}`
      : '/field-sessions';
    crumbs = [
      ...crumbs,
      <Link
        to={listPath}
        underline="hover"
        key="list-field-sessions"
        color="inherit"
        component={BaseLink}
      >
        <FormattedMessage
          id="fieldSessions.title"
          defaultMessage="Field sessions"
        />
      </Link>,
    ];

    if (!parts[1]) {
      return crumbs;
    }

    const sessionLabel = fieldSessionName || parts[1];
    if (parts[2] === 'finds') {
      return [
        ...crumbs,
        <Link
          to={`/field-sessions/${parts[1]}${location.search}`}
          underline="hover"
          key={`field-session-${parts[1]}`}
          color="inherit"
          component={BaseLink}
          noWrap
        >
          {sessionLabel}
        </Link>,
        <Typography key="field-session-finds">
          <FormattedMessage id="map.tab.finds" defaultMessage="Finds" />
        </Typography>,
      ];
    }

    return [
      ...crumbs,
      <Typography noWrap key={`field-session-${parts[1]}`}>
        {sessionLabel}
      </Typography>,
    ];
  }

  if (parts[0] === 'imports') {
    crumbs = [
      ...crumbs,
      <Link
        to="/imports"
        underline="hover"
        key="list-imports"
        color="inherit"
        component={BaseLink}
      >
        <FormattedMessage id="imports.title" defaultMessage="Imports" />
      </Link>,
    ];

    if (parts[1] === 'create') {
      return [
        ...crumbs,
        <Typography key="create-import">
          <FormattedMessage
            id="imports.workflow.title"
            defaultMessage="Import finds"
          />
        </Typography>,
      ];
    }

    if (parts[1]) {
      return [
        ...crumbs,
        <Typography noWrap key={`import-${parts[1]}`}>
          {parts[1]}
        </Typography>,
      ];
    }

    return crumbs;
  }

  if (parts[0] === 'contacts') {
    crumbs = [
      ...crumbs,
      <Link
        to="/contacts"
        underline="hover"
        key="contacts"
        color="inherit"
        component={BaseLink}
      >
        <FormattedMessage id="contacts.title" defaultMessage="Contacts" />
      </Link>,
    ];

    if (parts[1] && parts[1] !== 'requests' && parts[1] !== 'sent') {
      return [
        ...crumbs,
        <Typography noWrap key={`contact-${parts[1]}`}>
          {contactName || parts[1]}
        </Typography>,
      ];
    }

    return crumbs;
  }

  if (parts[0] === 'admin') {
    crumbs = [
      ...crumbs,
      <Link
        to="/admin/users"
        underline="hover"
        key="admin"
        color="inherit"
        component={BaseLink}
      >
        <FormattedMessage id="admin.title" defaultMessage="Admin" />
      </Link>,
    ];

    const section = parts[1] as keyof typeof adminSectionMessages;

    if (adminSectionMessages[section]) {
      return [
        ...crumbs,
        <Typography key={`admin-${section}`}>
          <FormattedMessage {...adminSectionMessages[section]} />
        </Typography>,
      ];
    }

    return crumbs;
  }

  if (parts[0] === 'f') {
    // we are in the find namespace
    if (!!siteName) {
      crumbs = [
        ...crumbs,
        <Link
          to="/s"
          underline="hover"
          key="list-sites"
          color="inherit"
          component={BaseLink}
        >
          <FormattedMessage id="map.tab.sites" defaultMessage="Sites" />
        </Link>,
        <Link
          to={`/s/${find.siteId}`}
          underline="hover"
          key={`site-${find.siteId}`}
          color="inherit"
          component={BaseLink}
          noWrap
        >
          {siteName}
        </Link>,
        <Link
          to={`/s/${find.siteId}/finds`}
          underline="hover"
          key="2"
          color="inherit"
          component={BaseLink}
        >
          <FormattedMessage id="map.tab.finds" defaultMessage="Finds" />
        </Link>,
        <Typography noWrap key={`find-${find.id}`}>
          {find.id}
        </Typography>,
      ];
    }
  }

  // we are elsewhere
  return crumbs;
};

const Breadcrumbs = () => {
  const intl = useIntl();
  const location = useLocation();
  const { findId, siteId } = useParams();
  // The object-group detail page is a nested route (`/o/*` → `groups/:groupId`),
  // so `useParams()` here doesn't see `groupId` — pull it from the pathname.
  const pathParts = location.pathname.slice(1).split('/');
  const groupId =
    pathParts[0] === 'o' && pathParts[1] === 'groups'
      ? pathParts[2]
      : undefined;
  const findGroupId =
    pathParts[0] === 'finds' && pathParts[1] === 'groups'
      ? pathParts[2]
      : undefined;
  const fieldSessionId =
    pathParts[0] === 'field-sessions' ? pathParts[1] : undefined;
  const contactId =
    pathParts[0] === 'contacts' &&
    pathParts[1] !== 'requests' &&
    pathParts[1] !== 'sent'
      ? pathParts[1]
      : undefined;

  const { data: findData } = useQuery(GET_MY_FIND_QUERY, {
    variables: { findId },
    fetchPolicy: 'cache-first',
    skip: !findId,
  });

  const find = findData?.getMyFind;

  const { data: siteData } = useQuery(GET_MY_SITE_QUERY, {
    variables: { siteId: siteId || find?.siteId },
    fetchPolicy: 'cache-first',
    skip: !(siteId || find?.siteId),
  });

  const siteName = siteData?.getMySite?.name;

  const { data: groupData } = useQuery(GET_OBJECT_GROUP_QUERY, {
    variables: { id: groupId },
    fetchPolicy: 'cache-first',
    skip: !groupId,
  });
  const objectGroupName = groupData?.getObjectGroup?.name;

  const { data: findGroupData } = useQuery(GET_FIND_GROUP_QUERY, {
    variables: { id: findGroupId },
    fetchPolicy: 'cache-first',
    skip: !findGroupId,
  });
  const findGroupName = findGroupData?.getFindGroup?.name;

  const { data: fieldSessionData } = useQuery(
    GET_MY_FIELD_SESSION_SUMMARY_QUERY,
    {
      variables: { id: fieldSessionId },
      fetchPolicy: 'cache-first',
      skip: !fieldSessionId,
    }
  );
  const fieldSessionName = fieldSessionData?.getMyFieldSession?.name;

  const { data: contactData } = useQuery(GET_MY_CONTACT_QUERY, {
    variables: { id: contactId },
    fetchPolicy: 'cache-first',
    skip: !contactId,
  });
  const contactName =
    contactData?.getMyContact?.displayName?.trim() ||
    contactData?.getMyContact?.email;

  const breadcrumbs = useMemo(
    () => [
      <Link
        underline="hover"
        to="/"
        key="1"
        color="inherit"
        component={BaseLink}
      >
        <HomeIcon color="action" />
      </Link>,
      ...getSectionCrumbs(
        location,
        siteName,
        find,
        objectGroupName,
        findGroupName,
        fieldSessionName,
        contactName
      ),
    ],
    [
      siteName,
      find,
      location,
      objectGroupName,
      findGroupName,
      fieldSessionName,
      contactName,
    ]
  );

  return (
    <BreadcrumbsComponent
      sx={breadcrumbsStyles}
      separator="›"
      aria-label={intl.formatMessage({
        id: 'common.breadcrumbs',
        defaultMessage: 'Breadcrumbs',
      })}
    >
      {breadcrumbs}
    </BreadcrumbsComponent>
  );
};

export { Breadcrumbs };
